import React, { useEffect, useMemo, useState } from "react";

import {
  Row,
  Col,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Alert,
  Spinner,
} from "reactstrap";

import PropTypes from "prop-types";

//redux
import { useSelector, useDispatch } from "react-redux";

import { Link } from "react-router-dom";
import withRouter from "../../components/Common/withRouter";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// actions
import { loginUser } from "../../store/actions";

// import images
import logo from "../../assets/images/NGK-LOGO.png";
import CarouselPage from "../AuthenticationInner/CarouselPage";
import { createSelector } from "reselect";
import {
  getRememberMePreference,
  getRememberedEmail,
  persistRememberMe,
} from "../../helpers/remember_me";
import { setPageMeta } from "../../utils/pageMeta";

const selectLogin = createSelector(
  (state: any) => state.login,
  (state) => ({
    error: state.error,
    loading: state.loading,
  })
);

const Login = (props: any) => {
  const dispatch = useDispatch();
  const { error, loading } = useSelector(selectLogin);
  const [passwordShow, setPasswordShow] = useState(false);
  const [rememberMe, setRememberMe] = useState(getRememberMePreference);
  const [submitting, setSubmitting] = useState(false);

  const rememberedEmail = useMemo(() => getRememberedEmail(), []);
  const busy = !!(loading || submitting);

  useEffect(() => {
    if (error) {
      setSubmitting(false);
    }
  }, [error]);

  useEffect(() => {
    setPageMeta({
      title: "Login | NGK Tours",
      description:
        "Sign in to the NGK Tours portal to manage bookings, operations, and travel services.",
      url: "https://ngktours.com/portal/login",
      canonical: "https://ngktours.com/portal/login",
    });
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: rememberedEmail || process.env.REACT_APP_DEV_LOGIN_EMAIL || "",
      password: rememberedEmail ? "" : process.env.REACT_APP_DEV_LOGIN_PASSWORD || "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values) => {
      if (busy) return;
      setSubmitting(true);
      persistRememberMe(rememberMe, values.email);
      dispatch(loginUser(values, props.router.navigate));
    },
  });

  return (
    <React.Fragment>
      <div className="auth-page">
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={4} md={5} className="col-xxl-3">
              <div className="auth-full-page-content d-flex p-sm-5 p-4">
                <div className="w-100">
                  <div className="d-flex flex-column h-100">
                    <div className="mb-4 mb-md-5 text-center">
                      <Link to="/dashboard" className="d-block auth-logo">
                        <img src={logo} alt="NGK Tours" height="28" />
                      </Link>
                    </div>
                    <div className="auth-content my-auto">
                      <div className="text-center">
                        <h5 className="mb-0">Welcome Back !</h5>
                        <p className="text-muted mt-2">
                          Sign in to continue to NGK Tours.
                        </p>
                      </div>
                      <Form
                        className="custom-form mt-4 pt-2 position-relative"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        {busy ? (
                          <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                            style={{
                              zIndex: 5,
                              background: "rgba(255, 255, 255, 0.7)",
                              borderRadius: 8,
                            }}
                          >
                            <Spinner color="primary" />
                            <div className="mt-2 text-primary fw-medium">
                              Signing in...
                            </div>
                          </div>
                        ) : null}
                        {error ? (
                          <Alert color="danger">
                            {typeof error === "string" ? error : String(error)}
                          </Alert>
                        ) : null}
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter email"
                            type="email"
                            autoComplete="username"
                            disabled={busy}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              !!(
                                validation.touched.email &&
                                validation.errors.email
                              )
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <div className="d-flex align-items-start">
                            <div className="flex-grow-1">
                              <Label className="form-label">Password</Label>
                            </div>
                            <div className="flex-shrink-0">
                              <Link to="/recoverpw" className="text-muted">
                                Forgot password?
                              </Link>
                            </div>
                          </div>
                          <div className="input-group auth-pass-inputgroup">
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              placeholder="Enter Password"
                              autoComplete="current-password"
                              disabled={busy}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                !!(
                                  validation.touched.password &&
                                  validation.errors.password
                                )
                              }
                            />
                            <button
                              onClick={() => setPasswordShow(!passwordShow)}
                              className="btn btn-light shadow-none ms-0"
                              type="button"
                              id="password-addon"
                              disabled={busy}
                            >
                              <i className="mdi mdi-eye-outline"></i>
                            </button>
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </div>

                        <div className="row mb-4">
                          <div className="col">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="remember-check"
                                checked={rememberMe}
                                disabled={busy}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setRememberMe(checked);
                                  persistRememberMe(
                                    checked,
                                    validation.values.email
                                  );
                                }}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="remember-check"
                              >
                                Remember me
                              </label>
                            </div>

                            <div className="mt-3 d-grid">
                              <button
                                className="btn btn-primary btn-block d-flex align-items-center justify-content-center"
                                type="submit"
                                disabled={busy}
                              >
                                {busy ? (
                                  <>
                                    <Spinner
                                      size="sm"
                                      className="me-2"
                                      color="light"
                                    />
                                    Signing in...
                                  </>
                                ) : (
                                  "Log In"
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </Form>

                    </div>
                    <div className="mt-4 mt-md-5 text-center">
                      <p className="mb-0">© {new Date().getFullYear()} NGK Tours.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <CarouselPage />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);
Login.propTypes = {
  history: PropTypes.object,
};
